import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

const isEmpty = (value) => {
  return value === undefined || value === null || value === '';
};
const buildTitle = (title, index) => {
  return title.replaceAll(/\bi\b/g, index);
};

type QuestionLibraryFieldArrayOptions = {
  name: string;
  appendItem: any;
  title?: string;
  template?: any[];
  fieldKeys?: string[];
  restrictions?: {
    min?: number;
    max?: number;
  };
  isAddEnabled?: boolean;
  isDeleteEnabled?: boolean;
};

export default function useQuestionLibraryFieldArray({
  name,
  appendItem,
  title = '',
  template = [],
  fieldKeys = [],
  restrictions = {},
  isAddEnabled = true,
  isDeleteEnabled = true,
}: QuestionLibraryFieldArrayOptions) {
  const { control, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name,
    control,
  });
  const layouts = useBuildLayouts({
    fields,
    title,
    fieldArrayName: name,
    template,
    fieldKeys,
  });

  useEffect(() => {
    if (restrictions.min) {
      if (fields.length < restrictions.min) {
        for (let i = 0; i < restrictions.min - fields.length; i++) {
          append(appendItem);
        }
      }
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const fieldsLength = useMemo(() => fields.length, [fields]);

  useEffect(() => {
    fields.forEach((field, index) => {
      setValue(`${name}.${index}`, field);
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [fieldsLength]);

  const disableAdd = useMemo(() => {
    if (isAddEnabled === false) {
      return true;
    }
    if (isEmpty(restrictions.max)) {
      return false;
    }
    return fields.length >= restrictions.max;
  }, [restrictions, fields, isAddEnabled]);

  const disableDelete = useMemo(() => {
    if (isDeleteEnabled === false) {
      return true;
    }
    if (isEmpty(restrictions.min)) {
      return false;
    }
    return fields.length <= restrictions.min;
  }, [restrictions, fields, isDeleteEnabled]);

  const handleAppend = (itemToAppend) => {
    append(itemToAppend);
  };

  const handleRemove = (index) => {
    remove(index);
  };

  const setValues = (name, value) => {
    setValue(name, value);
  };

  return {
    fields,
    handleAppend,
    handleRemove,
    disableAdd,
    disableDelete,
    setValues,
    layouts,
  };
}

const useBuildLayouts = ({
  fields,
  title,
  fieldArrayName,
  template,
  fieldKeys = [],
}) => {
  return useMemo(() => {
    return fields.map((field, index) => {
      const newTemplate = buildNewTemplate(
        structuredClone(template),
        field,
        fieldArrayName,
        index,
        [...fieldKeys],
        index === fields.length - 1,
      );
      return {
        title: title ? buildTitle(title, index + 1) : '',
        questions: newTemplate.template,
      };
    });
  }, [fields, template, fieldKeys, fieldArrayName, title]);
};

const buildNewTemplate = (
  template,
  field,
  fieldArrayName,
  index,
  fieldKeys,
  isLastTemplate,
) => {
  const fieldKeysSet = new Set(fieldKeys);
  const findAndReplaceName = (question, isLastQuestion) => {
    const { name, placeholders = [], conditions = {} } = question;

    if (name.includes('{i}')) {
      question.name = name.replace('{i}', index);
      if (field.questionAnswers) {
        const inputName = name.split('.').pop();
        const value = field.questionAnswers[inputName];
        if (value) {
          question.smartAnswer = value;
        }
      }
    }
    question.metadata = {
      index,
    };
    question.placeholders = findAndReplacePlaceholders(placeholders, index);
    question.conditions = findAndReplaceConditions(conditions, index);
    question.resolutions = findAndReplaceResolutions(
      question.resolutions,
      index,
    );

    if (fieldKeysSet.has(name)) {
      question.name = `${fieldArrayName}.${index}.${name}`;
      fieldKeysSet.delete(name);
    } else if (name.includes('fieldArray')) {
      question.name = name.replace('fieldArray', `${fieldArrayName}${index}`);
    }

    if (question.children) {
      question.children = question.children.map((child, i) => {
        return findAndReplaceName(child, i === question.children.length - 1);
      });
    } else if (isLastQuestion && isLastTemplate) {
      question.hasBottomBorder = false;
    }
    return question;
  };

  const newTemplate = template.map((question, index) =>
    findAndReplaceName(question, index === template.length - 1),
  );

  return { template: newTemplate, fieldKeys };
};

const createTemplatedQuestion = (question, index) => {
  const {
    name,
    placeholders = [],
    conditions = {},
    resolutions = [],
    children = [],
  } = question;
  question.name = name.replace('{i}', index);
  question.placeholders = findAndReplacePlaceholders(placeholders, index);
  question.conditions = findAndReplaceConditions(conditions, index);
  if (resolutions) {
    question.resolutions = resolutions.map((resolution) => {
      return findAndReplaceResolutions(resolution, index);
    });
  }
  if (children) {
    question.children = children.map((child) => {
      return createTemplatedQuestion(child, index);
    });
  }
  return question;
};

const findAndReplacePlaceholders = (resolutions, index) => {
  return resolutions.map((resolution) => {
    if (resolution.formDataKey?.includes('{i}')) {
      resolution.formDataKey = resolution.formDataKey.replace('{i}', index);
    }
    return resolution;
  });
};

const findAndReplaceConditions = (conditions, index) => {
  for (const key in conditions) {
    conditions[key] = findAndReplaceCondition(conditions[key], index);
  }
  return conditions;
};

const findAndReplaceCondition = (condition, index) => {
  if (condition.or || condition.and) {
    if (condition.or) {
      condition.or = condition.or.map((orCondition) => {
        return findAndReplaceCondition(orCondition, index);
      });
    } else {
      condition.and = condition.and.map((andCondition) => {
        return findAndReplaceCondition(andCondition, index);
      });
    }
    return condition;
  }

  if (condition.when) {
    condition.when = condition.when.replace('{i}', index);
    return condition;
  }
};
const findAndReplaceResolutions = (resolutions, index) => {
  return resolutions.map((resolution) => {
    if (resolution.resolutionType === 'followUpQuestion') {
      return createTemplatedQuestion(resolution, index);
    } else {
      resolution.conditions = findAndReplaceConditions(
        resolution.conditions,
        index,
      );
      return resolution;
    }
  });
};
